import { EventSubChatService } from './EventSubChatService';
import { type ParsedMessage } from './MessageParser';
import { ConnectionState, type ConnectionStatus } from './types';

export interface GlobalChatEvents {
  onMessage: (message: ParsedMessage) => void;
  onConnectionStateChange: (state: ConnectionState) => void;
  onError: (error: Error) => void;
}

/**
 * Global Chat Manager - Single source of truth for chat state
 */
export class GlobalChatManager {
  private static instance: GlobalChatManager | null = null;
  
  private chatService: EventSubChatService | null = null;
  private messageHandlers: Set<(message: ParsedMessage) => void> = new Set();
  private stateChangeHandlers: Set<(state: ConnectionState) => void> = new Set();
  private errorHandlers: Set<(error: Error) => void> = new Set();
  private isInitialized = false;

  private constructor() {
    console.log('[GlobalChatManager] Instance created');
  }

  public static getInstance(): GlobalChatManager {
    if (!GlobalChatManager.instance) {
      GlobalChatManager.instance = new GlobalChatManager();
    }
    return GlobalChatManager.instance;
  }

  public static reset(): void {
    GlobalChatManager.instance = null;
  }

  public async initialize(accessToken: string): Promise<void> {
    console.log('[GlobalChatManager] Initializing...');
    
    try {
      if (!this.chatService) {
        this.chatService = new EventSubChatService(accessToken);
        this.setupChatServiceHandlers();
      }

      if (!this.isInitialized) {
        await this.chatService.connect();
        
        const user = JSON.parse(localStorage.getItem('twitch_user') || '{}');
        if (user.login) {
          await this.chatService.joinChannel(user.login);
          console.log(`[GlobalChatManager] Connected to channel: ${user.login}`);
        }
        
        this.isInitialized = true;
      }
    } catch (error) {
      console.error('[GlobalChatManager] Initialization failed:', error);
      throw error;
    }
  }

  private setupChatServiceHandlers(): void {
    if (!this.chatService) return;

    this.chatService.onMessage((message: ParsedMessage) => {
      this.notifyMessageHandlers(message);
    });

    this.chatService.onConnectionStateChange((state: ConnectionState) => {
      this.notifyStateChangeHandlers(state);
    });

    this.chatService.onError((error: Error) => {
      this.notifyErrorHandlers(error);
    });
  }

  private notifyMessageHandlers(message: ParsedMessage): void {
    this.messageHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('[GlobalChatManager] Error in message handler:', error);
      }
    });
  }

  private notifyStateChangeHandlers(state: ConnectionState): void {
    this.stateChangeHandlers.forEach(handler => {
      try {
        handler(state);
      } catch (error) {
        console.error('[GlobalChatManager] Error in state change handler:', error);
      }
    });
  }

  private notifyErrorHandlers(error: Error): void {
    this.errorHandlers.forEach(handler => {
      try {
        handler(error);
      } catch (handlerError) {
        console.error('[GlobalChatManager] Error in error handler:', handlerError);
      }
    });
  }

  // Event subscription methods
  public onMessage(handler: (message: ParsedMessage) => void): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  public onConnectionStateChange(handler: (state: ConnectionState) => void): () => void {
    this.stateChangeHandlers.add(handler);
    return () => this.stateChangeHandlers.delete(handler);
  }

  public onError(handler: (error: Error) => void): () => void {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  // Public methods
  public async sendMessage(message: string): Promise<boolean> {
    if (!this.chatService || !this.isInitialized) {
      console.error('[GlobalChatManager] Chat service not initialized');
      return false;
    }

    const user = JSON.parse(localStorage.getItem('twitch_user') || '{}');
    if (!user.login) {
      console.error('[GlobalChatManager] No user login available');
      return false;
    }

    return this.chatService.sendMessage(user.login, message);
  }

  public async sendAnnouncement(message: string): Promise<void> {
    if (!this.chatService || !this.isInitialized) {
      throw new Error('Chat service not initialized');
    }

    const user = JSON.parse(localStorage.getItem('twitch_user') || '{}');
    if (!user.login) {
      throw new Error('No user login available');
    }

    await this.chatService.sendAnnouncement(user.login, message);
  }

  public getConnectionStatus(): ConnectionStatus {
    return this.chatService?.getConnectionStatus() || { 
      state: ConnectionState.DISCONNECTED,
      lastMessageTime: 0
    };
  }

  public getUserBadges() {
    return this.chatService?.getUserBadges() || [];
  }

  public isConnected(): boolean {
    return this.chatService?.isConnected() || false;
  }

  public disconnect(): void {
    if (this.chatService) {
      this.chatService.disconnect();
      this.isInitialized = false;
    }
  }
} 